import React, {useEffect, useState, useContext, useRef,useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams, Link} from 'react-router-dom';
import moment from 'moment';
import useColumnFilters from '../../../hooks/useColumnFilters';

import PageBase from "../../../components/PageBase";
import Datatable from "../../../components/Datatable";
import PopupWin from '../../../components/PopupWin/index.js';
import PopupButton from '../../../components/PopupButton';
import Toaster from "../../../components/Toaster";
import LoadingPanel from "../../../components/LoadingPanel";
import AccountManagerReportPopup from '../../../pages/Mapping/AccountManagerReportPopup'
import searchIcon from '../../../assets/search-glass.png';
import * as userActions from '../../../redux/User/actions.js'
import * as campaignActions from "../../../redux/Campaign/actions";
import  ContextProvider  from '../../../utils/ContextApi.js';
import * as ROUTES from "../../../utils/routes";
import Dropdown from '../../../components/FormFields/Dropdown';
import RelatedAccountsCell from './RelatedAccountsCell';
import './CampaignMembers.scss';
import EditOpportunity from '../../Opportunities/EditOpportunity/index.js';
import { getDisplayFields, getDefaultFields } from '../../../utils/fields/campaignMembers.js';
import ListManager from '../../../components/ListManager';
import {createColumnConfigs} from '../../../utils/fieldUtils.js';
import constants from '../../../utils/constants.js';
const CampaignMembers = props => {
	const { accountManagerReportData, setAccountManagerReportData, setHeader } = useContext(ContextProvider);
	const dispatch = useDispatch();
	const params = useParams();
	const members = useSelector(state => state.campaign.members)[params.id];
	const campaign = useSelector(state => state.campaign.campaigns?.find(c => c.campaign_id.toString() === params.id.toString()));
	const picklists = useSelector(state => state.opportunity.picklists);
	const [listColumns, setListColumns] = useState([]);
	const entityType = `${constants.DISPLAY_ENTITIES.CAMPAIGN_MEMBERS}`;
		const fieldsToUse = useMemo(() => {
		const columns = createColumnConfigs(getDisplayFields(listColumns.length === 0,listColumns));
		
		// Add custom selector only for related_accounts column
		columns.forEach(col => {
			if (col.id === 'related_accounts') {
				col.selector = (row) => {
					const val = row[col.id];
					if (!val || !Array.isArray(val)) return '(None)';
					
					// Map to names and filter out any undefined/null values
					const names = val
						.filter(item => item && item.name)
						.map(item => item.name);
					
					return names.length > 0 ? names.join(', ') : '(None)';
				};
			}
		});
		
		return columns;
	}, [listColumns]);


	// Add useColumnFilters hook

	// Flag to prevent multiple calls to process opportunities
	const processingOpportunityRef = useRef(false);

	const [search, setSearch] = useState('');
	const [loading, setLoading] = useState(true);
	const [selectedMembers, setSelectedMembers] = useState([]);
	const [status, setStatus] = useState([]);
	
	// States for sequential opportunity popups
	const [opportunitiesList, setOpportunitiesList] = useState([]); // Complete list of opportunities 
	const [currentOpportunity, setCurrentOpportunity] = useState(null); // Currently displayed opportunity
	const [remainingOpportunities, setRemainingOpportunities] = useState([]); // Opportunities waiting to be processed
	const [popupKey, setPopupKey] = useState(0); // Key to force popup remounting
	const [missedAccountDetails, setMissedAccountDetails] = useState([])
	const [filterResetKey, setFilterResetKey] = useState(0);
	const editOpportunityRef = useRef(null);

		const searchedData = useMemo(() => {
	if (!members) return [];
	if (!search) return members;
	const lowerSearch = search.toLowerCase();
	return members.filter(f => f.contact_name.toLowerCase().includes(lowerSearch));
	}, [members, search]);

	const {
		filters,
		sortBy,
		sortOrder,
		filteredData,
		handleFilterChange,
		resetWith,
		updateData,
		uniqueValuesByColumn,
		filterOrder
	} = useColumnFilters({
		data: searchedData || [],
		columns: fieldsToUse,
		storageKey: `campaign_${params.id}_members`,
		initialState: {}
	});

	const editOpportunityFooter = useMemo(() => (
		<>
			<PopupButton
				variant="secondary"
				onClick={() => setCurrentOpportunity(null)}
			>
				Cancel
			</PopupButton>
			<PopupButton
				variant="primary"
				onClick={() => editOpportunityRef.current?.save()}
			>
				Save
			</PopupButton>
		</>
	), []);

	useEffect(() => {
		const load = async () => {
			await dispatch(campaignActions.loadCampaignMembers(params.id));
			setLoading(false);
		};
		load();
	}, []);
	useEffect(() => {
		updateData(members);
	}, [members]);

	useEffect(() => {
		const loadAccounts = async () => {		
			// Use Promise.all to handle multiple requests in parallel
			await Promise.all(
				opportunitiesList.map(async (opp) => {
			  const filters = [
				{ key: 'account_id', operator: { key: 'is_exact' }, value: opp.accountId }
			  ];
			  
			  // Return the promise from the dispatch
			  return dispatch(
				userActions.getVendorAccounts(campaign.vendor, 1, null, filters, data => {
				  setMissedAccountDetails(prev => [...prev, data])
				})
			  );
			})
		  );
		};
		
		// Call the function
		loadAccounts();
		
		// Add all dependencies used inside the effect
	  }, [opportunitiesList, dispatch, campaign]);

	// Load statuses
	useEffect(() => {
		const load = async () => {
			await dispatch(campaignActions.getMemberStatus((data) => setStatus(data)));
		};
		load();
	}, []);

	const viewRelatedAccounts = (row) => {
		const { contact_email, vendor, contact_id } = row;
		console.log("generateAMReport - Enter");
		setLoading(true);
		const field = 'contact_email';
		const value = contact_email;
		const rows = [[{key: 0, field, type: "exactly", value, }]];
		dispatch(userActions.runReport(vendor, rows, data => accountManagerReportLoaded(data, contact_email, contact_id)));
	};

	const accountManagerReportLoaded = (result, accountManager, contactId) => {
		const relatedAccounts = members.find(m => m.contact_id === contactId).related_accounts || [];
		const fixedRelatedAccounts = relatedAccounts.map(a => ({account_id: a.id, account_name: a.name, isOld: true}))
		setLoading(false);
		setHeader({name:"Account Manager", value: accountManager, tableName: "accounts", metadata: {source: "Campaign members", campaignId: Number(params.id), contactId, relatedAccounts: fixedRelatedAccounts}});
		setAccountManagerReportData(result.data);
	};

	if(loading || members == null) {
		return (
			<PageBase title="Campaign Members" removeMargins hideRefresh>
				<LoadingPanel />
			</PageBase>
		)
	}

	const updateFilter = (value) => {
		setSearch(value.toLowerCase());
	};

	const memberRemoved = async () => {
		await dispatch(campaignActions.loadCampaignMembers(params.id));
		Toaster.showSuccess("Campaign member removed successfully");
	};

	const removeMember = async (row) => {
		await dispatch(campaignActions.removeCampaignContact(campaign.campaign_id, row.contact_id, row.contact_email, memberRemoved));
	};

	const renderActions = (row) => {
		return (
			<div
				onClick={() => removeMember(row)}
				style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
				Remove
			</div>
		)
	};

	// Fixed function to process the next opportunity in the queue
	const processNextOpportunity = () => {
		// Prevent multiple calls
		if (processingOpportunityRef.current) {
			return;
		}
		
		processingOpportunityRef.current = true;
		
		if (remainingOpportunities.length > 0) {
			// Get the next opportunity from the list
			const nextOpp = remainingOpportunities[0];
			const remaining = remainingOpportunities.slice(1);
						
			// Update all states in a single setTimeout to avoid race conditions
			setTimeout(() => {
				setRemainingOpportunities(remaining);
				setCurrentOpportunity(nextOpp);
				setPopupKey(prev => prev + 1);
				
				// Reset processing flag after a delay
				setTimeout(() => {
					processingOpportunityRef.current = false;
				}, 100);
			}, 50);
		} else {
			// No more opportunities to process
			setCurrentOpportunity(null);
			setRemainingOpportunities([]);
			setOpportunitiesList([]);
			
			// Reset processing flag
			processingOpportunityRef.current = false;
		}
	};

	// Callback for when an opportunity is saved
	const onOpportunitySaved = async () => {
		setLoading(true);
		
		// Process the next opportunity
		processNextOpportunity();
		
		// Other operations after saving
		// await dispatch(opportunityActions.loadOpportunityById(params.id))
		setLoading(false);
	};

	const updateMemberStatus = async (row, value) => {		
		const statusId = status.find(s => s.label === value).value;

		await dispatch(campaignActions.updateMemberStatus(campaign.campaign_id, row.contact_id, statusId, (data) => {
			if(data.status === 'ok') {
				Toaster.showSuccess("Status updated successfully");
				row.status = value;
				row.last_status_update = moment().utc().format('YYYY-MM-DD HH:mm:ss');
				setStatus([...status]);

				if(value === 'Meeting Set'){
					const relatedAccounts = members.filter(m => m.contact_id === row.contact_id)[0].related_accounts || [];

					if(relatedAccounts.length){
						// Create an array of opportunity objects with unique IDs
						const opportunitiesArray = relatedAccounts.map((ra, index) => ({
							id: `opp-${Date.now()}-${index}`, // Unique ID for each opportunity,
							accountId: ra.id,
							Customer_Status__c: 'In Contact With',
							End_Customer_Name: {label: ra.name, value: ra.name},
							Cloud_POC__Name: {label: row.contact_name, value: row.contact_email},
							Activity_Type__c: 'Net New',
							Leading_Cloud__c: campaign.vendor.toUpperCase(),
							StageName: 'Cloud Engagement',
							Priority__c: 'Target',
						}));
											
						if (opportunitiesArray.length > 0) {
							// Reset the processing flag
							processingOpportunityRef.current = false;
							
							// Set the first opportunity as current and store the rest
							setOpportunitiesList(opportunitiesArray);
							setRemainingOpportunities(opportunitiesArray.slice(1));
							setCurrentOpportunity(opportunitiesArray[0]);
							setPopupKey(prev => prev + 1); // Force popup remounting
						}
					}
				}
				return;
			}
			Toaster.showError("Failed to update status");
		}));
	};

	const renderRelatedAccounts = (row) => {
		return (
			<div
				onClick={() => viewRelatedAccounts(row)}
				style={{color: 'blue', textDecoration: 'underline', cursor: 'pointer'}}>
				View all Related Accounts
			</div>
		)
	};

	const membersOnchange = (e) => {
		console.log(e.target.checked);
		
		if (!e.target.checked) {
			setSelectedMembers([...selectedMembers, e.target.value])
			
		} else {
			setSelectedMembers(selectedMembers.filter(member => member !== e.target.value))
		}
	}

	// Update column renderers
	const index = fieldsToUse.findIndex(c => c.id === 'actions');
	if (index !== -1) {
	fieldsToUse[index].cell = renderActions;
	}
	const relatedAccountIndex = fieldsToUse.findIndex(c => c.id === 'view_all_related_accounts');
	if (relatedAccountIndex !== -1) {
	fieldsToUse[relatedAccountIndex].cell = renderRelatedAccounts;
	}

	const statusIndex = fieldsToUse.findIndex(c => c.id === 'status');
	if (statusIndex !== -1) {
		const statusIndex = fieldsToUse.findIndex(c => c.id === 'status');
		fieldsToUse[statusIndex].cell = row => {
			if(status) {
				return (
					<Dropdown 
						width={150} 
						items={status} 
						onChange={(value) => updateMemberStatus(row, value)} 
						selectedValue={row.status} 
						keyField="key" 
						labelField="label" 
					/>
				)
			}
		}
	}

	const contactIndex = fieldsToUse.findIndex(c => c.id === 'contact_name');
	if( contactIndex !== -1) {
		fieldsToUse[contactIndex].cell = row => (
			<Link 
				to={`/contact/${row.vendor}/${row.contact_id}`}
				state={{ 
					headerLinkTexts: "Campaign Members",
					headerLinkTarget: `/campaign/${campaign.campaign_id}/members`,
				}}
				style={{ color: 'blue', textDecoration: 'underline' }}
			>
				{row.contact_name}
			</Link>
		);
	}

	const relatedAccountsIndex = fieldsToUse.findIndex(c => c.id === 'related_accounts');
	if (relatedAccountsIndex !== -1) {
		fieldsToUse[relatedAccountsIndex].cell = row => {
			return (
				<RelatedAccountsCell 
					relatedAccounts={row.related_accounts} 
					vendor={row.vendor} 
					campaignId={campaign.campaign_id} 
				/>
			)
		};
	}
	
	// Handler for popup close with debounce protection
	const handlePopupClose = () => {
		console.log("Popup closed without saving, current ID:", currentOpportunity ? currentOpportunity.id : null);
		processNextOpportunity();
	};
	
	return (
		<PageBase 
			title="Campaign Members"
			removeMargins
			hideRefresh
			headerLinkText={campaign.campaign_name}
			headerLinkTarget={`/campaign/${campaign.campaign_id}`}
		>
			{
				accountManagerReportData != null &&
				<AccountManagerReportPopup />
			}
			<div className="campaigns-header">
				<div className="campaigns-search-box">
					<img alt="" src={searchIcon} style={{padding: '0 10px'}} />
					<input 
						className="campaigns-search-input" 
						type="text" 
						value={search} 
						onChange={e => updateFilter(e.target.value)} 
						placeholder="Search Campaigns..." 
					/>
				</div>
				<ListManager
					entityType={entityType}
					displayFields={getDisplayFields()}
					defaultFields={getDisplayFields(true).map(f => f.field)}
					onColumnsChange={setListColumns}
				/>
			</div>
			<div>
				<Datatable 
					gridHeight="calc(100vh - 185px)"
					key={filterResetKey}
					columns={fieldsToUse}
					data={filteredData}
					filters={filters}
					onFilterChange={handleFilterChange}
					sortBy={sortBy}
					sortOrder={sortOrder}
					uniqueValuesByColumn={uniqueValuesByColumn}
					filterOrder={filterOrder}
					allData={members}
				/>
			</div>
			
			{/* Opportunity popup with fixed sequential navigation */}
			<PopupWin
				key={`popup-opportunity-${popupKey}`}
				closeOnEscape={false}
				closeOnDocumentClick={false}
				visible={currentOpportunity !== null}
				onClose={handlePopupClose}
				title={`New Opportunity (${opportunitiesList.length - remainingOpportunities.length}/${opportunitiesList.length})`}
				titleStyle={{fontSize: '18px', margin: '0 0 25px 0'}}
				contentStyle={{width: '100%', height: '90%', padding: 0}}
				popupStyle={{width: '90%', maxWidth: '1600px', height: '84%', maxHeight: '800px', overflow: 'none'}}
				footerStyle={{borderTop: '1px solid #D3E1ED'}}
				footer={editOpportunityFooter}>
				{currentOpportunity && (
					<EditOpportunity
						ref={editOpportunityRef}
						key={`edit-opportunity-${currentOpportunity.id}`}
						autofillMode
						editMode={false}
						opportunity={currentOpportunity}
						onClose={onOpportunitySaved}
						missedAccountDetails={missedAccountDetails}
					/>
				)}
			</PopupWin>
		</PageBase>
	)
};

export default CampaignMembers;